import React, { useRef, useCallback } from 'react';
import { EventImage } from '@/components/common/EventImage';
import ShimmerView from '@/components/common/ShimmerView';
import type { ProfileEvent } from '@/components/common/EventPreviewModal';
import { COLORS } from '@/constants/colors';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { formatDuration } from '@/utils/volunteerHours';

export type { ProfileEvent };

export interface VolunteerActivity {
  id: string;
  title: string;
  location: string;
  date: string;
  image: string | null;
  department: string;
  hours: number;
  timeSlots: string;
  status: string;
  created_at: string;
  events?: ProfileEvent;
}

interface LatestActivitySectionProps {
  activities: VolunteerActivity[];
  loading: boolean;
  loadedActivities: number;
  isLoadingMore: boolean;
  onActivityClick: (activity: VolunteerActivity) => void;
  onLoadMore: () => void;
}

export function LatestActivitySection({
  activities,
  loading,
  loadedActivities,
  isLoadingMore,
  onActivityClick,
  onLoadMore,
}: LatestActivitySectionProps) {
  const activityScrollRef = useRef<HTMLDivElement>(null);
  const activityLoadTriggered = useRef(false);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrollThreshold = element.scrollWidth * 0.6;
    const isNearThreshold = element.scrollLeft + element.clientWidth >= scrollThreshold;
    
    if (isNearThreshold && !activityLoadTriggered.current && !isLoadingMore) {
      activityLoadTriggered.current = true;
      onLoadMore();
    }
  }, [onLoadMore, isLoadingMore]);

  React.useEffect(() => {
    activityLoadTriggered.current = false;
  }, [activities]);

  return (
    <Box px="20px" mb="20px">
      <Text as="h2" fontSize="20px" fontWeight="bold" color="#1e293b" mb="16px" letterSpacing="wide" mt="0">Latest Activity</Text>
      <Box bg="white" borderRadius="20px" p="20px" boxShadow="0 6px 20px rgba(100,116,139,0.1)" border="1px solid" borderColor="#f1f5f9">
        <Flex
          overflowX="auto"
          overflowY="hidden"
          py="8px"
          px="2px"
          ref={activityScrollRef}
          onScroll={handleScroll}
          css={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
        >
          {loading ? (
            <Flex w="160px" mr="16px" bg="white" borderRadius="12px" overflow="hidden" boxShadow="0 4px 12px rgba(0,0,0,0.08)" border="1px solid" borderColor="volyx.mediumGray" justify="center" align="center" h="140px" p="12px" flexShrink={0}>
              <Text fontSize="12px" color="volyx.textSecondary" textAlign="center" m="0">Loading...</Text>
            </Flex>
          ) : activities.length > 0 ? (
            activities.map((activity, index) => {
              const isLoaded = index < loadedActivities;
              return (
                <chakra.button
                  key={activity.id}
                  w="160px"
                  mr="16px"
                  borderRadius="12px"
                  overflow="hidden"
                  bg="white"
                  boxShadow="0 4px 12px rgba(0,0,0,0.08)"
                  cursor="pointer"
                  flexDir="column"
                  flexShrink={0}
                  border="none"
                  p="0"
                  _hover={{ boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}
                  _disabled={{ cursor: "not-allowed", opacity: 0.7 }}
                  onClick={() => isLoaded && onActivityClick(activity)}
                  disabled={!isLoaded}
                >
                  {!isLoaded ? (
                    <>
                      <ShimmerView style={{ width: '100%', height: 85, backgroundColor: COLORS.lightGray, borderRadius: '8px 8px 0 0' }} />
                      <Box p="10px">
                        <ShimmerView style={{ height: 12, backgroundColor: COLORS.lightGray, borderRadius: 6, marginBottom: 8 }} />
                        <Flex justify="space-between" align="center" mt="8px">
                          <ShimmerView style={{ width: '60%', height: 12, backgroundColor: COLORS.lightGray, borderRadius: 6 }} />
                          <ShimmerView style={{ width: '30%', height: 12, backgroundColor: COLORS.lightGray, borderRadius: 6 }} />
                        </Flex>
                      </Box>
                    </>
                  ) : (
                    <>
                      <EventImage
                        stored={activity.image}
                        alt={activity.title}
                        w="full"
                        h="85px"
                        objectFit="cover"
                      />
                      <Box p="10px">
                        <Text as="h3" fontWeight="bold" fontSize="14px" mb="4px" color="volyx.text" mt="0" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{activity.title}</Text>
                        <Flex justify="space-between" align="center" mt="8px">
                          <Text as="span" fontSize="12px" fontWeight="medium" color="volyx.textSecondary" flex="1" mr="8px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{activity.department}</Text>
                          <Text as="span" fontSize="12px" fontWeight="semibold" color="volyx.text">{formatDuration(activity.hours)}</Text>
                        </Flex>
                      </Box>
                    </>
                  )}
                </chakra.button>
              );
            })
          ) : (
            <Flex w="160px" mr="16px" bg="white" borderRadius="12px" overflow="hidden" boxShadow="0 4px 12px rgba(0,0,0,0.08)" border="1px solid" borderColor="volyx.mediumGray" justify="center" align="center" h="140px" p="12px" flexShrink={0}>
              <Text fontSize="14px" color="volyx.textSecondary" textAlign="center" fontStyle="italic" px="20px" m="0">No activities yet.</Text>
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
